import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete from 'fe-design-base/atoms/Autocomplete';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import {
  TIMECARD_SELECT_INPUTS,
  trackDropDownMenuClick,
} from 'features/timesheets/tracking';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
const { cx } = cxHelpers('RoleSelectInput');

const RoleSelectInput = ({
  roleId,
  setRoleId,
  roleOptions,
  locationName,
  addLocationRole,
  setChangesMade,
}) => {
  const [value, setValue] = useState('');

  const getRoleFromId = useCallback(
    id => roleOptions.find(role => role.value === id),
    [roleOptions]
  );

  useEffect(() => {
    setValue(getRoleFromId(roleId)?.label);
  }, [setValue, getRoleFromId, roleId]);

  const fixedOptions = useCallback(() => {
    if (roleId || !value) {
      return false;
    }
    return [
      {
        id: 'addRoleSelectInput',
        label: '',
        value,
        buttonProps: {
          children: (
            <Text
              textAlign="left"
              i18n="select_input.add_new_role"
              i18nProps={{ role: value || '' }}
            />
          ),
        },
      },
    ];
  }, [roleId, value]);

  const roleCreationCallback = useCallback(
    newRoleId => {
      setRoleId(newRoleId);
    },
    [setRoleId]
  );

  const createNewRole = useCallback(
    val => {
      addLocationRole(val, roleCreationCallback);
    },
    [addLocationRole, roleCreationCallback]
  );

  const handleChange = useCallback(
    (_, selection) => {
      if (selection?.id === 'addRoleSelectInput') {
        createNewRole(selection.value);
      }
      setRoleId(selection ? selection.value : null);
      setValue(selection?.label);
      setChangesMade(true);
    },
    [createNewRole, setChangesMade, setRoleId]
  );

  const handleRoleInputChange = useCallback(
    (_, val) => {
      setValue(val);
    },
    [setValue]
  );

  const trackOpen = useCallback(() => {
    trackDropDownMenuClick({
      properties: { drop_down: TIMECARD_SELECT_INPUTS.ROLE },
    });
  }, []);

  return (
    <Box row vcenter className={cx()}>
      <Box w={210}>
        <Autocomplete
          value={value}
          onChange={handleChange}
          onInputChange={handleRoleInputChange}
          options={roleOptions}
          fixedOptions={fixedOptions()}
          placeholder={toI18n(
            'timecard_modal_v3.header.edit_mode.role_placeholder'
          )}
          showCaret
          onOpen={partial(trackOpen)}
          dataTestId="role-select"
        />
      </Box>
      <Box ml={8}>
        <Text
          i18n="timecard_modal_v3.header.edit_mode.role_worked"
          i18nProps={{ location: locationName }}
        />
      </Box>
    </Box>
  );
};

RoleSelectInput.propTypes = {
  roleId: PropTypes.number,
  setRoleId: PropTypes.func.isRequired,
  roleOptions: PropTypes.array,
  locationName: PropTypes.string.isRequired,
  addLocationRole: PropTypes.func.isRequired,
  setChangesMade: PropTypes.func.isRequired,
};

export default RoleSelectInput;
